import { useEffect, useState } from 'react';
import { BordOneToneIcon } from 'components/NudosComponents';
import { nudosGray500 } from '../../../../../styles/styles.constants';
import ProductInformation from '../ProductInformation/ProductInformation';
import { IToolData } from 'types/products';
import GeneralTab from './GeneralTab/GeneralTab';
import { ICountryDetail } from 'types/countries';
import { QRDownloadButton } from 'components';
import ProductEditConditional from './ProductEditConditional/ProductEditConditional';
import ProductEditState from './ProductEditState/ProductEditState';
import ProductEditSkuAndSerial from './ProductEditSkuAndSerial/ProductEditSkuAndSerial';
import { LOCATION_PLACE_TYPE } from 'types/services';
import { getProductById } from 'services/toolsModule';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import ToolInfoTab from './ToolInfoTab/ToolInfoTab';
import WaitingFeature from './WaitingFeature/WaitingFeature';
import './ProductDetailModalContent.scss';

const ProductDetailModalContent = ({
  onClose,
  productIdSelected,
  listOfCountries,
  refreshToolsListing
}: IProductDetailModalContentProps) => {
  const mainTitleModalDefault = 'Interna de la herramienta';
  const [activeTab, setActiveTab] = useState<number>(0);
  const [mainTitleModal, setMainTitleModal] = useState<string>(mainTitleModalDefault);
  const [showInfoToolTab, setShowInfoToolTab] = useState<boolean>(true);
  const [showConditionalEdition, setShowConditionalEdition] = useState<boolean>(false);
  const [showStateEdition, setShowStateEdition] = useState<boolean>(false);
  const [showSkuAndSerialEdition, setShowSkuAndSerialEdition] = useState<boolean>(false);
  const [productData, setProductData] = useState<IToolData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [refreshProductData, setRefreshProductData] = useState<boolean>(false);
  const showArrowBack = showConditionalEdition || showStateEdition || showSkuAndSerialEdition;
  const handleShowConditionalEdition = () => {
    setMainTitleModal('Modificar la condición de la herramienta');
    setShowConditionalEdition(!showConditionalEdition);
    setShowInfoToolTab(false);
  };

  const handleShowStateEdition = () => {
    setMainTitleModal('Modificar el estado de la herramienta');
    setShowStateEdition(!showStateEdition);
    setShowInfoToolTab(false);
  };

  const handleShowSkuAndSerialEdition = () => {
    setMainTitleModal('Modificar datos de la herramienta');
    setShowSkuAndSerialEdition(!showSkuAndSerialEdition);
    setShowInfoToolTab(false);
  };

  const goBackArrowBack = () => {
    const hasAnyModalOpen = showConditionalEdition || showStateEdition || showSkuAndSerialEdition;

    if (hasAnyModalOpen) {
      setShowConditionalEdition(false);
      setShowStateEdition(false);
      setShowSkuAndSerialEdition(false);
      setMainTitleModal(mainTitleModalDefault);
      setShowInfoToolTab(true);
    }
  };

  const getProductData = async () => {
    setIsLoading(true);
    try {
      const productData = await getProductById(productIdSelected.toString());
      setProductData(productData);
      setRefreshProductData(false);
    } catch (error) {
      displayErrorNotification();
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Verifica si es necesario refrescar los datos del producto y los obtiene si es necesario.
   */
  const handleProductDataRefresh = () => {
    if (refreshProductData || productIdSelected) {
      getProductData();
    }
  };

  useEffect(() => {
    handleProductDataRefresh();
  }, [productIdSelected, refreshProductData]);

  const contentTabs = [
    {
      title: 'General',
      onClick: () => setActiveTab(0),
      component: (
        <GeneralTab
          productData={productData ?? null}
          listOfCountries={listOfCountries}
          handleShowConditionalEdition={handleShowConditionalEdition}
          handleShowStateEdition={handleShowStateEdition}
          handleShowSkuAndSerialEdition={handleShowSkuAndSerialEdition}
          isLoading={isLoading}
        />
      )
    },
    {
      title: 'Info. de la herramienta',
      onClick: () => setActiveTab(1),
      component: <ToolInfoTab productId={productIdSelected} />
    },
    {
      title: 'Historial',
      onClick: () => setActiveTab(2),
      component: <WaitingFeature />
    }
  ];

  const qrData = {
    productId: productIdSelected || productData?.id,
    serial: productData?.serial,
    orgName: productData?.organization?.organizationName
  };

  const disableQrDownload = ![
    LOCATION_PLACE_TYPE.NUDOS,
    LOCATION_PLACE_TYPE.DIAGNOSTIC,
    LOCATION_PLACE_TYPE.WARRANTY
  ].includes(productData?.location?.place as LOCATION_PLACE_TYPE);

  if (!productData) return null;
  return (
    <div className="productDetailModalContent">
      <div className="header">
        {showArrowBack && (
          <div className="returnArrow" onClick={goBackArrowBack}>
            <BordOneToneIcon variant="arrowLeft" customWidth="16px" stroke={nudosGray500} />
          </div>
        )}
        <p className="title">{mainTitleModal}</p>
        <div className="exitContentButton" onClick={onClose}>
          <BordOneToneIcon variant="x" customWidth="16px" stroke={nudosGray500} />
        </div>
      </div>
      <div className="content">
        <ProductInformation productData={productData} />
        {showInfoToolTab && (
          <div className="contentTabs">
            <div className="headerTabs">
              <div className="containerTabs">
                {contentTabs?.length > 0 &&
                  contentTabs?.map(tab => (
                    <div
                      className={`tab ${activeTab === contentTabs.indexOf(tab) ? 'active' : ''}`}
                      key={tab.title}
                      onClick={tab.onClick}
                    >
                      <p className="tabTitle">{tab.title}</p>
                    </div>
                  ))}
              </div>
              <QRDownloadButton QRData={qrData} isModeDark={true} disabled={disableQrDownload} />
            </div>
            <div className="containerContent">{contentTabs[activeTab].component}</div>
          </div>
        )}
        {showConditionalEdition && (
          <ProductEditConditional
            productData={productData}
            onClose={goBackArrowBack}
            setRefreshProductData={setRefreshProductData}
          />
        )}
        {showStateEdition && (
          <ProductEditState
            productData={productData}
            onClose={goBackArrowBack}
            setRefreshProductData={setRefreshProductData}
          />
        )}
        {showSkuAndSerialEdition && (
          <ProductEditSkuAndSerial
            productData={productData}
            onClose={goBackArrowBack}
            refreshToolsListing={refreshToolsListing}
            setRefreshProductData={setRefreshProductData}
          />
        )}
      </div>
    </div>
  );
};
interface IProductDetailModalContentProps {
  onClose: () => void;
  productIdSelected: number;
  listOfCountries: ICountryDetail[];
  refreshToolsListing: () => void;
}
export default ProductDetailModalContent;
